import { useEffect, useState } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import {
  PermissionStatus,
  PushNotifications,
} from '@capacitor/push-notifications';
import { Button, Card, Icon, Text } from '@nimbus-ds/components';
import { CopyIcon } from '@nimbus-ds/icons';
import { DeviceStorageType, DEVICE_INFO } from 'App/capacitor/device';
import { i18nConfig, Locale } from 'App/i18n';
import { getStorage } from 'App/storage';
import { COMPATIBLE_APP_VERSION } from 'App/version';
import { HeaderContent, IonPageStratus, Stack } from 'commons/components';
import { useClipboard } from 'commons/hooks';
import { useTranslationAccount } from '../hooks';

const isNativePlatform = Capacitor.isNativePlatform();

interface DeviceItemProps {
  item: string;
  value?: string;
}

function DeviceItemInfo({ item, value }: Readonly<DeviceItemProps>) {
  return (
    <Text lineClamp={5} wordBreak="break-all">
      <b>{item}:</b> {value || '-'}
    </Text>
  );
}

export function DeviceInfoPage() {
  const t = useTranslationAccount('deviceInfo');
  const copyToClipboard = useClipboard();
  const [appVersion, setAppVersion] = useState<string>('');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [storageInfo, setStorageInfo] = useState<DeviceStorageType | null>(
    null,
  );
  const [permissionStatus, setPermissionStatus] =
    useState<PermissionStatus | null>(null);
  const [locale, setLocale] = useState<Locale | null>(null);

  useEffect(() => {
    async function loadInfo() {
      try {
        const app = await App.getInfo();
        const device = await Device.getInfo();
        const storageInfo = await getStorage<DeviceStorageType>(DEVICE_INFO);
        const permissionStatus = await PushNotifications.checkPermissions();
        const localeStorage = await getStorage<Locale>(i18nConfig.STORAGE);

        setAppVersion(app.version);
        setDeviceInfo(device);
        setStorageInfo(storageInfo);
        setPermissionStatus(permissionStatus);
        setLocale(localeStorage);
      } catch (error) {
        console.error('Error obteniendo info:', error);
      }
    }

    if (isNativePlatform) loadInfo();
  }, []);

  const handleCopy = () => {
    const info = JSON.stringify({
      appVersion,
      compatibleAppVersion: COMPATIBLE_APP_VERSION,
      deviceInfo,
      storageInfo,
      permissionStatus,
      locale,
    });
    copyToClipboard(info, t('copySuccess'));
  };

  return (
    <IonPageStratus
      width="large"
      headerContent={<HeaderContent title={t('title')} />}
    >
      <Card>
        <Card.Body>
          <Stack column align="stretch" gap="2">
            <DeviceItemInfo item="App version" value={appVersion} />
            <DeviceItemInfo
              item="Compatible app version"
              value={COMPATIBLE_APP_VERSION}
            />
            <DeviceItemInfo item="Platform" value={deviceInfo?.platform} />
            <DeviceItemInfo item="OS" value={deviceInfo?.operatingSystem} />
            <DeviceItemInfo item="OS version" value={deviceInfo?.osVersion} />
            <DeviceItemInfo
              item="Manufacturer"
              value={deviceInfo?.manufacturer}
            />
            <DeviceItemInfo item="Model" value={deviceInfo?.model} />
            <DeviceItemInfo item="Device id" value={storageInfo?.deviceId} />
            <DeviceItemInfo item="Firebase token" value={storageInfo?.token} />
          </Stack>
        </Card.Body>
        <Card.Footer>
          <Button onClick={handleCopy} appearance="primary">
            <Icon color="currentColor" source={<CopyIcon size="small" />} />
            {t('button')}
          </Button>
        </Card.Footer>
      </Card>
    </IonPageStratus>
  );
}
